import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    // Reverse sync: when the customer scrolls or drags the carousel to a new
    // slide, select the matching color so the swatch, price, URL and add-to-cart
    // follow the image they are looking at. slideshow:select bubbles up from the
    // slideshow component. We only act on user-initiated movement ('scroll' /
    // 'drag'); programmatic 'select' changes (e.g. triggered by choosing a
    // swatch) are ignored to avoid a feedback loop.
    this.addEventListener('slideshow:select', this.#handleSlideSelect, { signal });

    // DH FIX 2026-08-24 — open on the colour the customer asked for. See the comment
    // on data-dh-initial-media in blocks/_product-media-gallery.liquid for the why.
    this.#openInitialMedia();
  }

  /**
   * Moves the carousel to the media named by data-dh-initial-media (the variant in
   * ?variant=). Retried on a short schedule because the slides have no width when
   * this first runs, and a scroll on a zero-width scroller clamps straight back to 0.
   * Stops the moment it lands, and gives up after ~1s rather than spinning.
   *
   * animate:false is load-bearing, not cosmetic: an animated jump of more than one
   * slide takes slideshow.js's placeholder-and-reorder path, which sets its private
   * #disabled flag and only clears it after the scroll finishes. An instant select
   * skips that path entirely, so it can never leave the gallery latched.
   */
  #openInitialMedia() {
    const mediaId = this.dataset.dhInitialMedia;
    if (!mediaId) return;

    const landed = () => {
      const slides = this.slideshow?.refs?.slides;
      const current = slides?.[this.slideshow.current];
      return current?.getAttribute('slide-id') === mediaId;
    };

    const attempt = () => {
      if (!this.isConnected || landed()) return true;

      const slideshow = this.slideshow;
      const scroller = slideshow?.refs?.scroller;
      if (!slideshow || !scroller || !slideshow.refs?.slides?.length) return false;

      // Nothing to scroll yet -- the slides have not been laid out.
      if (scroller.scrollWidth <= scroller.clientWidth) return false;

      slideshow.select({ id: mediaId }, undefined, { animate: false });

      return landed();
    };

    if (attempt()) return;

    // rAF catches the common case on the very next frame; the timers are the
    // fallback for when frames are throttled (background tab, reduced motion).
    requestAnimationFrame(() => attempt());
    for (const delay of [0, 50, 150, 400, 1000]) {
      setTimeout(() => attempt(), delay);
    }
  }

  #controller = new AbortController();

  /** @type {number} Debounce timer id for slide-driven variant selection. */
  #slideSelectTimer = 0;

  /** @type {Promise<Record<string, string> | null> | null} Cached mediaId -> color map. */
  #colorMapPromise = null;

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by selecting the variant's media in slideshow.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */

  //   #handleVariantUpdate = (event) => {
  //   const source = event.detail.data.html;

  //   if (!source) return;
  //   const newMediaGallery = source.querySelector('media-gallery');

  //   if (!newMediaGallery) return;

  //   // PDP: No change needed here; perform a standard replace
  //   this.replaceWith(newMediaGallery);
  // };
  #handleVariantUpdate = (event) => {
    const variantResource = event.detail.resource;
   
   
    if (!variantResource || !this.slideshow) {
      console.log('🖼️ Media Gallery: Missing variant resource or slideshow');
      return;
    }

    // Get the featured media ID from the variant
    const featuredMediaId = variantResource.featured_media?.id;
    console.log('🖼️ Media Gallery: Featured media ID:', featuredMediaId);

    if (featuredMediaId) { 
      // Select the variant's featured media in the slideshow
      this.slideshow.select({ id: featuredMediaId }, undefined, { animate: false });
    } else {
      console.log('🖼️ Media Gallery: No featured media ID found');
    }
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Handles a user-initiated slide change (scroll or drag) by selecting the
   * color variant whose image is now showing. Debounced so a fast multi-slide
   * scroll only acts on the slide it settles on.
   * @param {Event & { detail?: { trigger?: string, id?: string } }} event
   */
  #handleSlideSelect = (event) => {
    const detail = /** @type {any} */ (event).detail;
    if (!detail) return;

    // 'scroll' and 'drag' are always the customer moving the carousel by hand.
    // DH FIX 2026-08-24: 'select' also needs to count when it came from a real
    // click -- the thumbnail strip and the arrows both call slideshow.select(),
    // so clicking a thumbnail changed the big image while the swatch (and the
    // cart variant) stayed on the old colour. userInitiated is the safe test:
    // programmatic selects (a swatch click, a variant update, the zoom dialog)
    // pass no event, so they stay ignored and cannot loop back into the swatch
    // change this handler is about to make.
    const userSelect = detail.trigger === 'select' && detail.userInitiated === true;
    if (detail.trigger !== 'scroll' && detail.trigger !== 'drag' && !userSelect) return;

    const mediaId = detail.id;
    if (!mediaId) return;

    clearTimeout(this.#slideSelectTimer);
    this.#slideSelectTimer = setTimeout(() => {
      this.#selectColorForMedia(mediaId);
    }, 120);
  };

  /**
   * Selects the color swatch in this section's variant picker that corresponds
   * to the given media id, mimicking a real click on that swatch so all the
   * standard variant-change logic (price, availability, URL, cart) runs.
   * @param {string} mediaId
   */
  async #selectColorForMedia(mediaId) {
    const section = this.closest('.shopify-section');
    const picker = section?.querySelector('variant-picker');
    if (!picker) return;

    const colorMap = await this.#getColorMap(picker);
    if (!colorMap) return;

    const color = colorMap[String(mediaId)];
    if (!color) return;

    const radios = picker.querySelectorAll('input[type="radio"][data-option-value-id]');
    /** @type {HTMLInputElement | null} */
    let match = null;
    for (const radio of radios) {
      if (radio instanceof HTMLInputElement && radio.value === color) {
        match = radio;
        break;
      }
    }

    if (!match || match.checked) return;

    match.checked = true;
    match.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /**
   * Lazily builds and caches a { mediaId: colorValue } map for this product by
   * reading the product's lightweight JSON. Cached per element instance.
   * @param {Element} picker
   * @returns {Promise<Record<string, string> | null>}
   */
  #getColorMap(picker) {
    if (this.#colorMapPromise) return this.#colorMapPromise;

    const productUrl = (picker.getAttribute('data-product-url') || '').split('?')[0];
    if (!productUrl) {
      this.#colorMapPromise = Promise.resolve(null);
      return this.#colorMapPromise;
    }

    this.#colorMapPromise = fetch(`${productUrl}.js`)
      .then((response) => (response.ok ? response.json() : null))
      .then((product) => {
        if (!product || !Array.isArray(product.variants) || !Array.isArray(product.options)) return null;

        // Find which option the swatches represent (Color); default to the first option.
        let colorIndex = product.options.findIndex(
          (option) => String((option && option.name) || option).toLowerCase() === 'color'
        );
        if (colorIndex < 0) colorIndex = 0;
        const optionKey = `option${colorIndex + 1}`;

        /** @type {Record<string, string>} */
        const map = {};
        for (const variant of product.variants) {
          const featuredMediaId = variant.featured_media && variant.featured_media.id;
          if (featuredMediaId != null) {
            map[String(featuredMediaId)] = variant[optionKey];
          }
        }
        return map;
      })
      .catch(() => null);

    return this.#colorMapPromise;
  }

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
